"use client"

import Image from "next/image"
import Link from "next/link"
import { ChevronDown, ExternalLink, Facebook, MapPin, Menu, Phone, X, Youtube } from "lucide-react"
import { useEffect, useRef, useState } from "react"

type MenuItem = {
  label: string
  href: string
}

const navItemClass = "inline-flex items-center gap-1.5 whitespace-nowrap px-2 py-2.5 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring lg:px-2.5 xl:text-sm"
const mobileItemClass = "rounded-sm px-4 py-3 text-sm font-medium text-foreground transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"

const specialtyItems: MenuItem[] = [
  { label: "Amino Acid/Nutrition Therapy", href: "/specialties/amino-acid-nutrition-therapy/" },
  { label: "Anxiety & Trauma", href: "/specialties/treatment-for-anxiety-trauma-kalamazoo/" },
  { label: "Counseling for Women", href: "/specialties/counseling-for-women-kalamazoo/" },
  { label: "Highly Sensitive People/Empaths", href: "/specialties/highly-sensitive-people-empaths/" },
  { label: "Holistic Healing", href: "/specialties/holistichealingmindfulness-kalamazoo/" },
  { label: "Nutrition & Mental Well-being", href: "/specialties/nutrition-mental-health-holistic/" },
]

const resourceItems: MenuItem[] = [
  { label: "Healing Resources", href: "/resources/healing-resources/" },
  { label: "Diversity & Inclusion Policy", href: "/resources/diversity-inclusion-policy/" },
  { label: "Disclaimer", href: "/resources/disclaimer/" },
  { label: "News", href: "/newsevents-kalamazoo/" },
  { label: "Blog", href: "/blog/" },
]

function DropdownNavItem({ label, items }: { label: string; items: MenuItem[] }) {
  return (
    <div className="group relative">
      <button
        type="button"
        className={navItemClass}
        aria-haspopup="menu"
        aria-label={`${label} menu`}
      >
        {label}
        <ChevronDown aria-hidden="true" className="size-3.5 text-primary transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
      </button>
      <div className="pointer-events-none invisible absolute left-0 top-full z-50 min-w-64 translate-y-2 border border-border bg-background p-2 opacity-0 shadow-theme-card transition-all duration-200 group-hover:pointer-events-auto group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
        {items.map((item) => (
          <Link
            key={item.label}
            href={item.href}
            role="menuitem"
            className="block px-3 py-2.5 text-sm text-muted-foreground transition hover:bg-secondary hover:text-foreground focus:bg-secondary focus:outline-none"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  )
}

function TherapyCloudNavItem() {
  const [isOpen, setIsOpen] = useState(false)
  const [hasFocus, setHasFocus] = useState(false)
  const itemRef = useRef<HTMLDivElement>(null)
  const menuId = "therapy-cloud-menu"
  const therapyCloudUrl = "https://www.therapycloud.com/"
  const menuVisibilityClass = isOpen
    ? "pointer-events-auto visible translate-y-0 opacity-100"
    : "pointer-events-none invisible translate-y-2 opacity-0"

  useEffect(() => {
    if (!isOpen) return

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (itemRef.current && !itemRef.current.contains(event.target as Node)) setIsOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false)
        setHasFocus(false)
      }
    }

    document.addEventListener("pointerdown", closeOnOutsidePointer)
    document.addEventListener("keydown", closeOnEscape)

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer)
      document.removeEventListener("keydown", closeOnEscape)
    }
  }, [isOpen])

  return (
    <div
      ref={itemRef}
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => {
        if (!hasFocus) setIsOpen(false)
      }}
      onFocusCapture={() => {
        setHasFocus(true)
        setIsOpen(true)
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setHasFocus(false)
          setIsOpen(false)
        }
      }}
    >
      <button
        type="button"
        className={navItemClass}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen((open) => !open)}
      >
        Therapy Cloud
        <ExternalLink aria-hidden="true" className="size-3.5 text-primary" />
      </button>
      <div
        id={menuId}
        role="dialog"
        aria-label="Therapy Cloud on-demand support"
        className={`absolute right-0 top-full z-50 w-80 max-w-[calc(100vw-2.5rem)] rounded-card border border-border bg-card p-4 shadow-theme-card transition-all duration-200 ${menuVisibilityClass}`}
      >
        <a
          href={therapyCloudUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Visit TherapyCloud"
          onClick={() => setIsOpen(false)}
          className="block rounded-card focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-card"
        >
          <Image
            src="/images/1789051731775-0.png"
            alt="TherapyCloud logo"
            width={1200}
            height={628}
            sizes="(min-width: 1024px) 18rem, calc(100vw - 2.5rem)"
            className="block h-auto w-full rounded-card border border-border transition-opacity hover:opacity-95"
          />
        </a>
        <p className="mt-4 text-sm leading-6 text-muted-foreground">
          Can&apos;t work with me because of where you&apos;re located, insurance restrictions, or because you&apos;re just not ready for therapy yet? No problem! You can find access to content I have created to help offer therapeutic support on demand on my TherapyCloud profile here.
        </p>
        <a
          href={therapyCloudUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setIsOpen(false)}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-button bg-primary px-4 py-3 text-center text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-card"
        >
          Learn More
          <ExternalLink aria-hidden="true" className="size-4" />
        </a>
      </div>
    </div>
  )
}

function MobileMenuLink({ item, onNavigate, className = "" }: { item: MenuItem; onNavigate: () => void; className?: string }) {
  return (
    <Link className={`${mobileItemClass} ${className}`} href={item.href} onClick={onNavigate}>
      {item.label}
    </Link>
  )
}

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileSpecialtiesOpen, setMobileSpecialtiesOpen] = useState(false)
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [navEngaged, setNavEngaged] = useState(false)
  const closeMobileMenu = () => {
    setMobileOpen(false)
    setMobileSpecialtiesOpen(false)
    setMobileResourcesOpen(false)
  }

  useEffect(() => {
    const updateScrollState = () => setHasScrolled(window.scrollY > 24)
    updateScrollState()
    window.addEventListener("scroll", updateScrollState, { passive: true })

    return () => window.removeEventListener("scroll", updateScrollState)
  }, [])

  const hideContactBar = hasScrolled && !navEngaged

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border/70 bg-background text-foreground">
      <div
        className="site-header-contact-bar border-b border-border/70 bg-card"
        data-collapsed={hideContactBar}
        aria-hidden={hideContactBar}
      >
        <div className="mx-auto flex max-w-theme flex-wrap items-center justify-between gap-x-6 gap-y-1 px-5 py-2 text-xs text-muted-foreground lg:px-8">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <a
              className="inline-flex items-center gap-2 rounded-sm bg-secondary/70 px-2.5 py-1.5 transition hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              href="tel:2692541211"
            >
              <Phone aria-hidden="true" className="size-3.5 text-primary" />
              <span><span className="font-semibold text-foreground">Call Us:</span> 269-254-1211</span>
            </a>
            <span className="hidden items-center gap-2 border-l border-border pl-5 sm:inline-flex">
              <MapPin aria-hidden="true" className="size-3.5 text-primary" />
              <span><span className="font-semibold text-foreground">Our Address:</span> 4155 S 9th St. Kalamazoo, MI 49009</span>
            </span>
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <span>Find us on Social Media:</span>
            <a
              className="grid size-6 place-items-center rounded-full text-primary transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"
              href="https://www.facebook.com/InnerPeaceCounselingKalamazoo/"
              aria-label="Facebook"
              target="_blank"
              rel="noreferrer"
            >
              <Facebook aria-hidden="true" className="size-4" />
            </a>
            <a
              className="grid size-6 place-items-center rounded-full text-primary transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"
              href="https://www.youtube.com/channel/UCZPwOxFQ-pkneyM94bzSrLA"
              aria-label="YouTube"
              target="_blank"
              rel="noreferrer"
            >
              <Youtube aria-hidden="true" className="size-4" />
            </a>
          </div>
        </div>
      </div>

      <a
        className="site-header-schedule-ribbon"
        href="https://innerpeacecounseling.clientsecure.me/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>Schedule Now</span>
        <ExternalLink aria-hidden="true" className="size-4" />
      </a>

      <nav
        className="border-b border-border/70 bg-background"
        aria-label="Primary navigation"
        onMouseEnter={() => setNavEngaged(true)}
        onMouseLeave={() => setNavEngaged(false)}
        onFocus={() => setNavEngaged(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setNavEngaged(false)
        }}
      >
        <div className="mx-auto flex min-h-24 max-w-theme items-center gap-5 px-5 py-3 lg:px-8">
          <Link href="/" className="group shrink-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-4 focus:ring-offset-background" aria-label="Inner Peace Counseling, PLC home">
            <Image
              src="/images/logo.png"
              alt="Inner Peace Counseling, PLC — Kalamazoo holistic therapist"
              width={210}
              height={149}
              priority
              className="h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </Link>

          <div className="hidden min-w-0 flex-1 items-center justify-end gap-0.5 lg:flex">
            <Link className={navItemClass} href="/team_member/ashleycarteryoungblood/">About Me</Link>
            <Link className={navItemClass} href="/team_member/therapy-dog-kalamazoo/">Therapy Dog, Kaia</Link>
            <DropdownNavItem label="Specialties" items={specialtyItems} />
            <Link className={navItemClass} href="/feesinsurancefaq/">Fees/Insurance/FAQ</Link>
            <DropdownNavItem label="Resources" items={resourceItems} />
            <Link className={navItemClass} href="/gallery/">Gallery</Link>
            <TherapyCloudNavItem />
            <Link className="inline-flex whitespace-nowrap rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring xl:text-sm" href="/contactus/">
              Contact Us
            </Link>
          </div>

          <button
            type="button"
            className="ml-auto inline-flex size-10 items-center justify-center rounded-sm border border-border text-foreground transition hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring lg:hidden"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? <X aria-hidden="true" className="size-5" /> : <Menu aria-hidden="true" className="size-5" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div id="mobile-navigation" className="border-t border-border/70 bg-background px-5 py-4 lg:hidden">
          <div className="mx-auto grid max-w-theme gap-1">
            <MobileMenuLink item={{ label: "About Me", href: "/team_member/ashleycarteryoungblood/" }} onNavigate={closeMobileMenu} />
            <MobileMenuLink item={{ label: "Therapy Dog, Kaia", href: "/team_member/therapy-dog-kalamazoo/" }} onNavigate={closeMobileMenu} />
            <div className="flex items-center gap-1">
              <MobileMenuLink item={{ label: "Specialties", href: "/specialties/" }} onNavigate={closeMobileMenu} className="flex-1" />
              <button
                type="button"
                className="inline-flex size-12 shrink-0 items-center justify-center rounded-sm border border-border text-primary transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label={mobileSpecialtiesOpen ? "Collapse Specialties menu" : "Expand Specialties menu"}
                aria-expanded={mobileSpecialtiesOpen}
                aria-controls="mobile-specialties-menu"
                onClick={() => setMobileSpecialtiesOpen((open) => !open)}
              >
                <ChevronDown aria-hidden="true" className={`size-4 transition-transform ${mobileSpecialtiesOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            <div id="mobile-specialties-menu" aria-hidden={!mobileSpecialtiesOpen} className={`${mobileSpecialtiesOpen ? "grid" : "hidden"} ml-4 gap-1 border-l border-border py-1 pl-2`}>
              {specialtyItems.map((item) => (
                <MobileMenuLink key={item.label} item={item} onNavigate={closeMobileMenu} />
              ))}
            </div>
            <MobileMenuLink item={{ label: "Fees/Insurance/FAQ", href: "/feesinsurancefaq/" }} onNavigate={closeMobileMenu} />
            <div className="flex items-center gap-1">
              <MobileMenuLink item={{ label: "Resources", href: "/resources/" }} onNavigate={closeMobileMenu} className="flex-1" />
              <button
                type="button"
                className="inline-flex size-12 shrink-0 items-center justify-center rounded-sm border border-border text-primary transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label={mobileResourcesOpen ? "Collapse Resources menu" : "Expand Resources menu"}
                aria-expanded={mobileResourcesOpen}
                aria-controls="mobile-resources-menu"
                onClick={() => setMobileResourcesOpen((open) => !open)}
              >
                <ChevronDown aria-hidden="true" className={`size-4 transition-transform ${mobileResourcesOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            <div id="mobile-resources-menu" aria-hidden={!mobileResourcesOpen} className={`${mobileResourcesOpen ? "grid" : "hidden"} ml-4 gap-1 border-l border-border py-1 pl-2`}>
              {resourceItems.map((item) => (
                <MobileMenuLink key={item.label} item={item} onNavigate={closeMobileMenu} />
              ))}
            </div>
            <MobileMenuLink item={{ label: "Gallery", href: "/gallery/" }} onNavigate={closeMobileMenu} />
            <a
              className={`${mobileItemClass} inline-flex items-center justify-between gap-2`}
              href="https://www.therapycloud.com/"
              target="_blank"
              rel="noreferrer"
              onClick={closeMobileMenu}
            >
              Therapy Cloud
              <ExternalLink aria-hidden="true" className="size-4 text-primary" />
            </a>
            <Link className="bg-primary px-4 py-3 text-center text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring" href="/contactus/" onClick={closeMobileMenu}>Schedule Now</Link>
          </div>
        </div>
      )}
      </header>
    </>
  )
}
